import React, { FunctionComponent } from 'react';
import {
  Button,
  Container,
  Form,
  Icon,
  Loader,
  Message,
  TextArea,
} from 'semantic-ui-react';
import { ProReviewInvitationMailsFormFields } from './pro-review-invitation-emails-form-field.type';
import { useFormikContext } from 'formik';
import FieldError from 'components/field-error/field-error';
import 'reactjs-tiptap-editor/style.css';
import { useEditor, EditorContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import HardBreak from '@tiptap/extension-hard-break';
import Bold from '@tiptap/extension-bold';
import Image from '@tiptap/extension-image';
import styled from 'styled-components';
import Link from '@tiptap/extension-link';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import HorizontalRule from '@tiptap/extension-horizontal-rule';

interface Props {
  fetchEmails: (forceDefault?: boolean) => Promise<
    | {
        firstEmail: string;
        dunningEmail: string;
      }
    | undefined
  >;
  loading: boolean;
}

const StyledEditorContent = styled(EditorContent)`
  border: 1px solid #d4d4d5;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 15px;
  min-height: 200px; // Ajout d'une hauteur minimale pour une meilleure expérience

  .ProseMirror:focus {
    outline: none !important;
  }
`;

const ProReviewInvitationsEmailsFormView: FunctionComponent<Props> = ({
  fetchEmails,
  loading,
}) => {
  const {
    values,
    handleSubmit,
    submitForm,
    isSubmitting,
    setFieldValue,
    errors,
  } = useFormikContext<ProReviewInvitationMailsFormFields>();

  const extensions = [
    Document,
    Paragraph,
    Text,
    HardBreak,
    Bold,
    Image,
    Link,
    Italic,
    Underline,
    Strike,
    BulletList,
    OrderedList,
    ListItem,
    TextAlign,
    Color,
    TextStyle,
    HorizontalRule,
    Image,
  ];
  const editorFirstEmail = useEditor(
    {
      extensions,
      onBlur: ({ editor }) => {
        setFieldValue('customReviewInvitationEmail', editor.getHTML());
      },
      content: values.customReviewInvitationEmail,
    },
    [values.customReviewInvitationEmail],
  );

  const editorDunningEmail = useEditor(
    {
      extensions,
      onBlur: ({ editor }) => {
        setFieldValue('customReviewInvitationDunningEmail', editor.getHTML());
      },
      content: values.customReviewInvitationDunningEmail,
    },
    [values.customReviewInvitationDunningEmail],
  );

  const [step, setStep] = React.useState<number>(1);

  const [htmlMode, setHtmlMode] = React.useState<boolean>(false);

  return (
    <Form onSubmit={handleSubmit}>
      <Message>
        <p>
          Vous avez à votre disposition plusieurs variables pour personnaliser
          les mails. Pour les utiliser, il suffit d’insérer la variable
          souhaitée dans votre texte en respectant bien cette écriture :{' '}
          <strong>[nom_de_la_variable]</strong>.
        </p>
        <ul>
          <li>
            <strong>{'[sujet: contenu du sujet]'}</strong> : sujet du mail
            (modifiez uniquement le contenu après &quot;sujet:&quot;)
          </li>
          <li>
            <strong>{'[nom]'}</strong> : prénom du particulier
          </li>
          <li>
            <strong>{'[prenom]'}</strong> : nom du particulier
          </li>
          <li>
            <strong>{'[lien]'}</strong> : lien vers le formulaire (obligatoire)
          </li>
          <li>
            <strong>{'[signature]'} </strong> : nom et prénom du contact ou
            &quot;L&apos;équipe&quot; si plusieurs contacts + nom de la fiche
          </li>
          <li>
            <strong>{'[metiers]'} </strong>: métier principal / métier bis de la
            fiche
          </li>
        </ul>
      </Message>
      <Container
        style={{
          marginBottom: '15px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button.Group>
          <Button
            color="grey"
            basic={step !== 1}
            type="button"
            onClick={() => setStep(1)}
          >
            Premier mail
          </Button>
          <Button
            color="grey"
            type="button"
            onClick={() => setStep(2)}
            basic={step !== 2}
          >
            Mail de rappel
          </Button>
        </Button.Group>

        <Button
          type="button"
          onClick={() => setHtmlMode((current) => !current)}
        >
          <Icon name={htmlMode ? 'pencil' : 'code'} />
          {htmlMode ? 'Mode classique' : 'Mode HTML'}
        </Button>
      </Container>
      {loading ? (
        <>Chargement...</>
      ) : (
        <>
          {step === 1 && (
            <>
              {htmlMode ? (
                <TextArea
                  value={values.customReviewInvitationEmail}
                  onChange={(_, { value }) =>
                    setFieldValue('customReviewInvitationEmail', String(value))
                  }
                  rows={25}
                />
              ) : (
                <StyledEditorContent editor={editorFirstEmail} />
              )}
              <FieldError name="customReviewInvitationEmail">
                {errors.customReviewInvitationEmail}
              </FieldError>
            </>
          )}

          {step === 2 && (
            <>
              {htmlMode ? (
                <TextArea
                  value={values.customReviewInvitationDunningEmail}
                  onChange={(_, { value }) =>
                    setFieldValue(
                      'customReviewInvitationDunningEmail',
                      String(value),
                    )
                  }
                  rows={25}
                />
              ) : (
                <StyledEditorContent editor={editorDunningEmail} />
              )}
              <FieldError name="customReviewInvitationDunningEmail">
                {errors.customReviewInvitationDunningEmail}
              </FieldError>
            </>
          )}
        </>
      )}
      <Container textAlign="right" style={{ marginTop: '15px' }}>
        <Button
          type="reset"
          onClick={() => {
            fetchEmails(true).then((emails) => {
              if (emails) {
                setFieldValue('customReviewInvitationEmail', emails.firstEmail);
                setFieldValue(
                  'customReviewInvitationDunningEmail',
                  emails.dunningEmail,
                );
              }
            });
          }}
        >
          <Icon name="undo" />
          Réinitialiser
        </Button>
        <Button
          type="button"
          onClick={submitForm}
          loading={isSubmitting}
          disabled={isSubmitting}
          primary
        >
          <Icon name="save" />
          Enregistrer
        </Button>
      </Container>
    </Form>
  );
};

export default ProReviewInvitationsEmailsFormView;
